import axios from 'axios'
import { getDifferenceInMinutes } from '../actions/dateUtility';

import { getRelatedPagesByTaxonomyId } from './reservoir'
import ProcessType from '../enums/ProcessType'
import { getConfiguration } from '../helpers/configuration'
import { isAfter } from 'date-fns'

axios.interceptors.request.use(
  function(config) {
    if (!(config.url.startsWith('https') || config.url.startsWith('http'))) {
      let headers = config.headers

      let tokenType = 'Bearer'
      let token = sessionStorage.getItem('JW_TOKEN')

      if (config.url === '/api/jwt/token') {
        tokenType = 'Basic'

        token = sessionStorage.getItem('token')
        if (!token) {
          token = btoa(getConfiguration('jwtUsername') + ':' + getConfiguration('jwtPassword'))
          sessionStorage.setItem('token', token)
        }
      }

      if (token !== null && typeof token !== 'undefined') {
        Object.assign(headers, {
          Authorization: tokenType + ' ' + token
        })

        config.headers = headers
      }
    }

    return config
  },
  function(error) {
    // Do something with request error
    return Promise.reject(error)
  }
)

const freshGet = () => {
  return get(getRequest, params).then(response => {
    sessionStorage.setItem(cacheKey, JSON.stringify(response))

    return response
  })
}

/**
 *
 * @param {string} cacheKey Unique identifier for the cached resource
 * @param {string} getRequest The URL where the resource is stored
 * @param {object} params
 * @returns {Promise}
 */
const cacheGet = (cacheKey, getRequest, params = {}) => {
  if (!sessionStorage.getItem(cacheKey)) {
    return get(getRequest, params).then(response => {
      sessionStorage.setItem(cacheKey, JSON.stringify(response))

      return response
    })
  }

  try {
    const payload = JSON.parse(sessionStorage.getItem(cacheKey))
    return new Promise(resolve => {
      resolve(payload)
    })
  } catch (error) {
    return freshGet()
  }
}

export const get = (url, options = {}) => {
  return axios
    .get(
      url,
      Object.assign({}, options, {
        headers: {
          'X-Request-With': 'XMLHttpRequest'
        }
      })
    )
    .then(response => response.data)
    .catch(handleErrorResponse)
}

export const post = (url, params) => {
  return axios
    .post(url, null, params, {
      headers: {
        'X-Request-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.data)
    .catch(handleErrorResponse)
}

export const put = (url, params) => {
  return axios
    .put(url, params, {
      headers: {
        'X-Request-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.data)
    .catch(handleErrorResponse)
}

export const handleErrorResponse = error => {
  if (error.response && error.response.status === 403) {
    localStorage.clear()
    sessionStorage.clear()

    window.location.reload()
  } else {
    throw error
  }
}

export const getJwtAuthenticationToken = () => {
  return axios
    .get('/api/jwt/token')
    .then(response => {
      return response.data
    })
    .then(data => {
      if (data.token === null || data.token === undefined || data.token === '' || data.token === '') {
        return [false, true]
      }
      sessionStorage.setItem('JW_TOKEN', data.token)
      return [true, false]
    })
    .catch(error => {
      return [false, true]
    })
}

export const getReservoirAuthToken = () => {
  return axios
    .get(
      '/api/reservoir/getAuthToken',
      Object.assign(
        {},
        {},
        {
          headers: {
            'X-Request-With': 'XMLHttpRequest'
          }
        }
      )
    )
    .then(response => response.data)
    .then(data => {
      sessionStorage.setItem('cms_url', `${data.protocol}://${data.host}`)
      sessionStorage.setItem('access_token', JSON.stringify(data.token))

      return data
    })
}

export const getReservoirApiUrl = (urlPath = '') => {
  if (!sessionStorage.getItem('cms_url')) {
    return getReservoirAuthToken().then(data => `${data.protocol}://${data.host}/${urlPath}`)
  }

  return new Promise(resolve => {
    resolve(`${sessionStorage.getItem('cms_url')}/${urlPath}`)
  })
}

export const getAccessToken = () => {
  if (sessionStorage.getItem('access_token')) {
    let token = JSON.parse(sessionStorage.getItem('access_token'))
    if (isAfter(new Date(token.expiresAtString), new Date())) {
      return new Promise(resolve => {
        resolve(token.tokenValue)
      })
    }
  }

  return getReservoirAuthToken().then(authToken => authToken.token.tokenValue)
}

export const putEcafDetails = params => {
  return put('/api/student/ecafDetails', params)
}

export const getEcafDetails = courseId => {
  return get('/api/student/ecafDetails', {
    params: {
      courseId: courseId
    }
  })
}

export const putSAHelpDetails = params => {
  return put('/api/student/saHelp', params)
}

export const getSAHelpDetails = courseId => {
  return get('/api/student/saHelp', {
    params: {
      courseId: courseId
    }
  })
}

export const putStudentReport = params => {
  return put('/api/student/report', params)
}

export const putPersonalDetails = params => {
  sessionStorage.removeItem('PERSONAL_DETAILS')
  return put('/api/student/personalData', params)
}

export const getPersonalDetails = () => {
  return cacheGet('PERSONAL_DETAILS', '/api/student/personalData')
}

export const getPronounsDetails = () => {
  return cacheGet('PRONOUNS_DETAILS', '/api/systemData/sone/pronouns')
}

export const getAffirmedGenderDetails = () => {
  return cacheGet('AFFIRMED_GENDER_DETAILS', '/api/systemData/sone/affirmedGender')
}

export const getAttendanceModeCodesDetails = () => {
  return cacheGet('ATTENDED_MODE_CODE', '/api/systemData/sone/attendanceModeCodes')
}

export const getAttendanceModeDescription = (attendanceModeList, unitattendanceModeCd) => {
  // console.log(this.state.attendanceModeList);
  const attendanceModeCd = attendanceModeList.find(attendanceModeList => attendanceModeList.code === unitattendanceModeCd)
  if (attendanceModeCd) {
    return attendanceModeCd.description
  }
  return attendanceModeCd
}

export const getCurrentUser = () => {
  return get('/api/student/me')
}

export const createFavouritedTile = tileId => {
  sessionStorage.removeItem('FAVOURITED_TILES')
  return axios.post('/api/student/favourites', {
    tileId: tileId
  })
}

export const getFavouritedTiles = () => {
  return cacheGet('FAVOURITED_TILES', '/api/student/favourites').then(response => response.map(tile => tile.tileId))
}

export const deleteFavouritedTile = tileId => {
  sessionStorage.removeItem('FAVOURITED_TILES')
  return axios.delete(`/api/student/favourites/${tileId}`)
}

export const getDismissedNotifications = () => {
  return get('/api/student/notificationDismissals').then(response => response.map(notification => notification.notificationId))
}

export const createDismissedNotification = notificationId => {
  return axios.post('/api/student/notificationDismissals', {
    notificationId: notificationId
  })
}

export const getNotifications = () => {
  return get('/api/student/notifications')
}

export const putNotifications = params => {
  return put('/api/student/notifications', params)
}

export const getStudyPeriodWeightings = () => {
  const cacheName = 'getStudyPeriodWeightings'

  return cacheGet(cacheName, '/api/properties/studyPeriodWeightings')
}

export const getReferences = () => {
  if (sessionStorage.getItem('reference_data')) {
    return new Promise(resolve => {
      resolve(JSON.parse(sessionStorage.getItem('reference_data')))
    })
  }

  return get('/api/reference').then(response => {
    sessionStorage.setItem('reference_data', JSON.stringify(response))

    return response
  })
}

export const getCourses = () => {
  let offer = JSON.parse(sessionStorage.getItem('COURSES'))
  if (offer && lessThanThirty(offer.date)) {
    return new Promise(resolve => {
      resolve(JSON.parse(sessionStorage.getItem('COURSES')).response)
    })
  }

  return get('/api/student/courses').then(response => {
    sessionStorage.setItem('COURSES', JSON.stringify({ response: response, date: new Date() }))

    return response
  })
}

export const getCoursesSummary = (qParams = {}) => {
  let coursesSummary = JSON.parse(sessionStorage.getItem('COURSES_SUMMARY'))
  if (coursesSummary && lessThanThirty(coursesSummary.date)) {
    return new Promise(resolve => {
      resolve(coursesSummary.response)
    })
  }

  return get('/api/courses/summary', {params: qParams}).then(response => {
    sessionStorage.setItem('COURSES_SUMMARY', JSON.stringify({ response: response, date: new Date() }))

    return response
  })
}

export const getCourseDetails = (courseId, qParams = {}) => {
  return get('/api/courses/' + courseId, {params: qParams})
}

export const getSoneOfferByCourseId = courseId => {
  return get('/api/student/courseDetails?courseId=' + courseId, {})
}

export const getCohorts = courseId => {
  const cacheName = 'COHORT_CDS_' + courseId

  if (sessionStorage.getItem(cacheName)) {
    return new Promise(resolve => {
      resolve(JSON.parse(sessionStorage.getItem(cacheName)))
    })
  }

  return get('/api/student/cohorts', {
    params: {
      courseId: courseId
    }
  }).then(response => {
    sessionStorage.setItem(cacheName, JSON.stringify(response))

    return response
  })
}

export const getAllEnrolledUnits = parentSSPNo =>
  get('/api/student/enrolment', {
    params: {
      parentSSPNumber: parentSSPNo
    }
  })

export const getActiveEnrolmentCalendars = (cohortCd = [], processType = []) => getEnrolmentCalendars(cohortCd, processType, true)

export const getStudentCalendarsByCourseId = courseId =>
  getStudentCalendars({
    courseCd: courseId
  })

export const getStudentCalendars = params =>
  get('/api/calendar/student', {
    params: params
  })

export const getEnrolmentCalendars = (cohortCd = [], processType = [], active = null) => {
  let cacheName = 'ENROLMENT_CALENDARS'
  if (cohortCd) {
    cacheName += `_${cohortCd}`
  }
  if (processType) {
    cacheName += `_${processType}`
  }
  if (active !== null) {
    cacheName += `_${active ? 'ACTIVE' : 'INACTIVE'}`
  }

  if (sessionStorage.getItem(cacheName)) {
    return new Promise(resolve => {
      resolve(JSON.parse(sessionStorage.getItem(cacheName)))
    })
  }

  let params = {}

  if (processType && processType.length > 0) {
    params['processType'] = processType.join(',')
  }

  if (cohortCd && cohortCd.length > 0) {
    params['cohortCd'] = cohortCd.join(',')
  }

  if (active !== null) {
    params['active'] = active
  }

  return get(`/api/calendar/enrolment`, {
    params: params
  }).then(response => {
    sessionStorage.setItem(cacheName, JSON.stringify(response))

    return response
  })
}

export const getActiveAddAndDropCalendars = (cohortCd = []) => {
  return getActiveEnrolmentCalendars(cohortCd, [ProcessType.AddUnit, ProcessType.DropUnit])
}

export const getEnrolmentDeadlines = parentSSPNumber => {
  return get('/api/student/enrolmentDeadlines?parentSSPNumber=' + parentSSPNumber)
}

export const getOfferData = courseId => {
  return get('/api/offer/details?courseCode=' + courseId)
}

export const getUsiDetails = () => {
  return get('/api/student/usiDetails')
}

export const putUsiDetails = params => {
  return put('/api/student/usiDetails', params)
}

export const getProperties = () => {
  return get('/api/properties/MyVU')
}

export const getStudentProgress = courseId => {
  return get('/api/student/progress', {
    params: {
      courseId: courseId
    }
  })
}

export const getTeachingCalendars = (from = '', to = '') => {
  let cacheItemName = 'CALENDAR_TEACHING'
  let params = {}

  if (from) {
    cacheItemName = `${cacheItemName}_FROM_${from}`
    params['from'] = from
  }

  if (to) {
    cacheItemName = `${cacheItemName}_TO_${to}`
    params['to'] = to
  }

  if (!sessionStorage.getItem(cacheItemName)) {
    return get('/api/calendar/teaching', {
      params: params
    }).then(response => {
      sessionStorage.setItem(cacheItemName, JSON.stringify(response))

      return response
    })
  }

  return new Promise(resolve => {
    resolve(JSON.parse(sessionStorage.getItem(cacheItemName)))
  })
}

export const getSurveyTemplate = (surveyId, locationCode) => {
  let AFS_template = JSON.parse(sessionStorage.getItem('AFS_template'))

  if (AFS_template && getDifferenceInMinutes(AFS_template.date, new Date()) < 30) {
    return new Promise(resolve => {
      resolve(JSON.parse(sessionStorage.getItem('AFS_template')).response)
    })
  }

  return get('/api/afs/template', {
    params: {
      surveyId: surveyId,
      locationCode: locationCode
    }
  }).then(response => {
    sessionStorage.setItem('AFS_template', JSON.stringify({ response: response, date: new Date() }))

    return response
  })
}

export const getAdvisor = (surveyId, locationCode) => {
  return get('/api/afs/advisor', {
    params: {
      surveyId: surveyId,
      locationCode: locationCode
    }
  })
}

export const submitSurvey = (surveyId, request, status = '1') => {
  //console.log('submit survey api call %o status %o', surveyId, status)
  return axios.post('/api/afs/template?surveyId=' + surveyId, {
    status: status,
    questions: request
  })
}

export const getSubmittedSurvey = surveyId => {
  return get('/api/afs/submittedSurvey?surveyId=' + surveyId)
}

export const getPreferredCourse = () => {
  const cacheName = 'PREFERRED_COURSE'
  return cacheGet(cacheName, '/api/student/preferredCourse')
}

export const updatePreferredCourse = courseId => {
  sessionStorage.removeItem('PREFERRED_COURSE')
  return put('/api/student/preferredCourse', {
    courseId: courseId
  })
}

export const getPreferredSlider = courseId =>
  get('/api/student/preferredSlider', {
    params: {
      courseId
    }
  })

export const updatePreferredSlider = (courseId, sliderId) =>
  put('/api/student/preferredSlider', {
    courseId,
    sliderId
  })

export const updateMyResourcesTertiaryItems = (taxonomyId, cohort) => {
  const cacheName = `SECONDARY_PAGE_${taxonomyId}_${cohort}`

  if (sessionStorage.getItem(cacheName)) {
    return new Promise(resolve => {
      resolve(JSON.parse(sessionStorage.getItem(cacheName)))
    })
  }

  return getRelatedPagesByTaxonomyId(taxonomyId, cohort)
    .then((relatedPages = []) => {
      return relatedPages.map(page => {
        return {
          id: page.id,
          name: page.attributes.title,
          heading: page.attributes.field_pageheading,
          taxonomyId: taxonomyId
        }
      })
    })
    .then((relatedPages = []) => {
      sessionStorage.setItem(cacheName, JSON.stringify(relatedPages))

      return relatedPages
    })
}

export const getSoneFundingByCourseSSPNo = (courseSSPNo, liabilityCode, stuCitizenCode) => {
  let queryParams = {}

  if (courseSSPNo) {
    queryParams['sspNumber'] = courseSSPNo
  }

  if (liabilityCode) {
    queryParams['liabilityCode'] = liabilityCode
  }

  if (stuCitizenCode) {
    queryParams['stuCitizenCode'] = stuCitizenCode
  }

  return get('/api/student/fundingDetails', {
    params: queryParams
  })
}

function lessThanThirty(date) {
  //30 mins
  return Math.abs(getDifferenceInMinutes(date, new Date())) < 30
}

export const getActiveMaintenancePeriods = () => {
  return get('/api/content/maintenancePeriods', {
    params: {
      activeOnly: true
    }
  }).then(response => response.data)
}

export const getCourseProgress = courseSSPNo => {
  //  return new Promise((resolve) => resolve(
  //      {"requiredToComplete":288,"achievedAsCompleted":228,"achievedAsAdvanceStanding":0,
  //        "enrolledCP":36,"remaining":60,"totalAchieved":228,
  //        "status":"INACTIVE","completionPercentage":0.79,"electronicCoeEndDate":null,"isEligibleToViewProgress":"Y"}))
  //}
  return get('/api/student/courseProgress', {
    params: {
      courseSSPNo: courseSSPNo
    }
  })
}

export const getEnrolmentWorkflows = (cohortCds = []) => {
  return get('/api/content/enrolmentWorkflows', {
    params: {
      activeOnly: true,
      cohortCd: cohortCds.join(',')
    }
  }).then(response => response.data)
}

export const getSliders = (courseId, admissionCategory = [], courseAttemptStatus, teachingPeriods = [], locationCode) => {
  let cacheName = `SLIDERS_${courseId}`
  if (teachingPeriods.length > 0) {
    cacheName += `_${teachingPeriods.sort().join('-')}`
  }

  if (sessionStorage.getItem(cacheName)) {
    return new Promise(resolve => {
      resolve(JSON.parse(sessionStorage.getItem(cacheName)))
    })
  }

  return get('/api/content/sliders', {
    params: {
      admissionCategory: admissionCategory.join(','),
      teachingPeriods: teachingPeriods.join(','),
      courseAttemptStatus: courseAttemptStatus,
      locationCode
    }
  }).then((sliders = []) => {
    sessionStorage.setItem(cacheName, JSON.stringify(sliders))
    return sliders
  })
}

export const postAddressSearch = (countryCode, address) => {
  return post('/api/address/search', {
    params: {
      countryCode: countryCode,
      address: address
    }
  }).then(response => response)
}

export const postValidateAddress = (globalAddressKey, countryCode) => {
  return post(`/api/address/retrieve`, {
    params: {
      globalAddressKey: globalAddressKey,
      countryCode: countryCode
    }
  }).then(response => response)
}